// chat/actions.ts — the chat control executor for Rightsize. The client router (chat/brain.ts) maps
// ONE user message → ONE ChatAction; this file EXECUTES that action against the on-device store
// (src/store.ts), scoped to the currently active child. Everything here is LOCAL SQLite over Tauri
// IPC — no network, no billing — EXCEPT "recap" and "ask", whose text was already produced by the
// EXISTING live endpoints (sizingRecap / askSizes in ../brain) inside the router; this file only
// renders that text, it never calls the AI itself.
//
// Actions are derived from THIS app's own store: SizeRecord has create + update + delete (→ add_size,
// edit_size, delete_size), InventoryItem/Child CRUD is left to the existing UI (out of scope — kept
// tight to the app's primary repeatable record, the sizing log), plus a free local search over both
// size records and bought-ahead inventory, plus the app's two already-shipped AI features.
//
// HITL discipline: delete_size is NEVER executed from a chat turn on its own — the Chat UI holds it
// in `pending` and only runs it on an explicit Confirm tap.

import { store, prettyDate, type SizeRecordPatch } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "outgrown";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, BrainError } from "../brain";
import { addInstruction } from "./brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact result row rendered inline in a chat bubble (search matches).
export interface ResultRow {
  id: string;
  primary: string;
  secondary?: string;
  trailing?: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_size"; category: string; sizeLabel: string; sizeValue: number | null; brand: string; note: string }
  | { kind: "edit_size"; id: string; label?: string; patch: SizeRecordPatch }
  | { kind: "delete_size"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "search"; query: string }
  | { kind: "recap"; text: string } // text already produced by the existing sizingRecap() call in the router
  | { kind: "ask"; question: string; answer: string } // answer already produced by the existing askSizes() call
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean;  // a size record changed → the main screen should refresh
  rows?: ResultRow[]; // matches to render inline
}

export interface ExecCtx {
  childId: string;
  childName: string;
  /** The current chat text, set per-send so save_to_cloud can store the conversation. */
  transcript?: string;
}

export async function executeAction(a: ChatAction, ctx: ExecCtx): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_size": {
        const r = await store.createSizeRecord({
          child_id: ctx.childId,
          category: a.category,
          size_label: a.sizeLabel,
          size_value: a.sizeValue,
          brand: a.brand,
          note: a.note,
        });
        const brandNote = r.brand ? ` (${r.brand})` : "";
        return { ok: true, message: `Logged ${ctx.childName}'s ${r.category} size ${r.size_label}${brandNote}.`, changed: true };
      }

      case "edit_size": {
        await store.updateSizeRecord(a.id, a.patch);
        const newLabel = a.patch.size_label ? ` to ${a.patch.size_label}` : "";
        return { ok: true, message: `Updated ${a.label ?? "that size"}${newLabel}.`, changed: true };
      }

      case "delete_size":
        await store.deleteSizeRecord(a.id);
        return { ok: true, message: `Removed ${a.label ?? "that size"} from ${ctx.childName}'s history.`, changed: true };

      case "search": {
        const [records, inventory] = await Promise.all([
          store.listSizeRecords(ctx.childId),
          store.listInventory(ctx.childId),
        ]);
        const q = a.query.toLowerCase();
        const recMatches = records.filter((r) =>
          r.category.toLowerCase().includes(q) ||
          r.brand.toLowerCase().includes(q) ||
          r.note.toLowerCase().includes(q) ||
          r.size_label.toLowerCase().includes(q)
        );
        const invMatches = inventory.filter((i) =>
          i.category.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q) ||
          i.note.toLowerCase().includes(q)
        );
        const rows: ResultRow[] = [
          ...recMatches.map((r) => ({
            id: r.id,
            primary: `${r.category} ${r.size_label}`,
            secondary: r.brand || undefined,
            trailing: prettyDate(r.recorded_on),
          })),
          ...invMatches.map((i) => ({
            id: i.id,
            primary: `${i.category} ${i.size_label} · bought ahead`,
            secondary: i.description || undefined,
            trailing: i.for_season || undefined,
          })),
        ];
        const total = recMatches.length + invMatches.length;
        return {
          ok: true,
          message: total
            ? `${total} match${total !== 1 ? "es" : ""} for “${a.query}”.`
            : `Nothing in ${ctx.childName}'s sizes or bought-ahead items matches “${a.query}”.`,
          rows,
        };
      }

      case "recap":
        return { ok: true, message: a.text || "Not enough logged yet to recap — add a few more sizes over time." };

      case "ask":
        return { ok: true, message: a.answer || "I couldn't work that out from the logged sizes." };

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my size history" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole collection). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
